class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    const firstProduct = document.getElementById('CartDrawer-Item-1')?.dataset.productId;
    this.getCartRecommendations(firstProduct);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getCartRecommendations(productId) {
    return fetch(`/recommendations/products?product_id=${productId}&limit=4&section_id=cart-recommendations`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => response.text())
      .then(html => {
        this.querySelector('.cart-drawer__recommendations').innerHTML = html;
      }).then(() => {
        const prevArrowsHorizontal = document.querySelector('.cart-recommendations .swiper-btn--prev');
        const nextArrowsHorizontal = document.querySelector('.cart-recommendations .swiper-btn--next');
        const swiper = new Swiper(".cart-recommendations .swiper", {
            slidesPerView: '1',
            spaceBetween: 15,
            watchOverflow: true,
            loop: true,
            slidesOffsetAfter: 0,
            slidesOffsetBefore: 1,
            navigation: {
              nextEl: nextArrowsHorizontal,
              prevEl: prevArrowsHorizontal,
            },
            pagination: {
              el: document.querySelector('.cart-recommendations .swiper .swiper-bullets'),
              clickable: true,
              type: 'bullets'
            }
          });
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
async function convertItemToSubscription(button, lineKey, sellingPlanId) {
  button.innerText = 'Updating...';
  button.style.pointerEvents = 'none';

  try {
    // 1. Remove the current one-time item
    const removeRes = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });

    // 2. Add the subscription version
    // We use the first part of the key (the variant ID) to re-add
    const variantId = lineKey.split(':')[0];
    const addRes = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          selling_plan: sellingPlanId
        }],
        sections: ['cart-drawer', 'cart-icon-bubble'] // Tell Shopify which sections to re-render
      })
    });

    const parsedState = await addRes.json();

    // 3. Trigger Portland's native re-render
    // This utilizes the existing method in your CartDrawer class
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && parsedState.sections) {
      cartDrawer.renderContents(parsedState);
    } else {
      window.location.reload(); // Fallback
    }

  } catch (error) {
    console.error('Subscription Swap Error:', error);
    button.innerText = 'Error - Try again';
    button.style.pointerEvents = 'auto';
  }
}